import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import styles from './browse-stylepacks.module.css';
import Portal from '@ui/portal/portal';
import SearchInput from '@ui/search-input/search-input';
import Icon from '@ui/icons/icon';
import Button from '@ui/buttons/button/button';
import { ICONS } from '@ui/icons/types';
import { useCallback, useEffect, useState } from 'react';
import Nav from './components/nav';
import { StyleCategories, type Stylepack } from '@/types/stylepacks.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectStylepacks } from '@/store/slices/stylepacks/selectors';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import toastTrigger from '@ui/toast/toast-trigger';
import { addToRecentStylepacks } from '../utils/stylepack-utils';
import { useRecentStylepacks } from './use-recent-stylepacks';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

const canShowStylepack = (category: StyleCategories, pack: Stylepack, recent: string[] | null, searchTerm: string) => {
  const searchMatch = !searchTerm || pack.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());

  if (category === StyleCategories.all) {
    return searchMatch;
  }

  if (category === StyleCategories.recent) {
    if (recent?.length) {
      return recent.includes(pack.stylePack) && searchMatch;
    }

    return searchMatch; // nothing recent but do not show empty list
  }

  return pack.categories.includes(category) && searchMatch;
};

export default function BrowseStylepacks() {
  const [category, setCategories] = useState(StyleCategories.all);
  const [searchTerm, setSearchTerm] = useState('');
  const stylepacks = useAppSelector(selectStylepacks);
  const workingProject = useAppSelector(getWorkingProject);
  const [saving, setSaving] = useState(false);
  const recentStylepackIds = useRecentStylepacks();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const targetSession = workingProject?.sessions[0];

  useEffect(() => {
    if (location.state && location.state.category) {
      setCategories(location.state.category);
    }
    // only used on load to preselect a category when navigating back to this view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCategory = useCallback(
    (category: StyleCategories) => {
      navigate('.', { state: { category }, replace: true, relative: 'path' });
      setCategories(category);
    },
    [navigate]
  );

  const chooseStylepack = useCallback(
    async (stylepackId: string) => {
      if (!targetSession) return;

      try {
        setSaving(true);
        await dispatch(
          saveWorkingProjectSessionByField({
            uuid: targetSession.uuid,
            streaming_options: {
              ...targetSession.streaming_options,
              stylepackId,
            },
          })
        );

        addToRecentStylepacks(stylepackId);
        navigate('../', { relative: 'path' });
      } catch (e) {
        console.error(e);
        toastTrigger('Error selecting stylepack', { type: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [dispatch, navigate, targetSession]
  );

  const goBack = () => {
    navigate('..', { relative: 'path' });
  };

  return (
    <Portal id="stylepacks-main" className={styles.main}>
      <Container maxWidth="1440px" margin="auto" className={styles.container} padding="0 96px">
        <Container className="flex jc-space-between" tagName="header" padding="48px 0 24px 0" width="100%">
          <Text tag="h3" className="H3">
            Stylepacks
          </Text>
          <Container className="flex ai-center" gap="8px">
            <SearchInput expandOnFocus onSubmit={setSearchTerm} onClear={() => setSearchTerm('')} />
            <Button
              onClick={goBack}
              design="LocalBttnSec"
              className="flex ai-center"
              padding="14px"
              height="40px"
              width="40px"
            >
              <Icon name={ICONS.CLOSE} size={12} />
            </Button>
          </Container>
        </Container>
        <Container>
          <Nav category={category} setCategory={setCategory} />
        </Container>
        <Container className={styles.grid}>
          {stylepacks?.map((pack) => {
            if (canShowStylepack(category, pack, recentStylepackIds, searchTerm)) {
              return (
                <div className={styles.stylepackCard} key={pack.stylePack}>
                  <div className={styles.stylepackCardPreview}>
                    <div
                      style={{
                        backgroundImage: `url(${pack.videos[0].thumbnailUrl})`,
                      }}
                      className={styles.stylepackCardBackground}
                    ></div>
                    <div className={styles.stylepackCardActions}>
                      <Link to={`../preview/${pack.stylePack}`} relative="path">
                        <Button
                          backgroundColor={getThemeColorCSSVariable()}
                          designSize="medium"
                          className="flex ai-center"
                        >
                          <Text
                            color={getThemeColorContrastCSSVariable(
                              EColorVariablesV2.buttonText,
                              EColorVariablesV2.primary
                            )}
                            className="flex ai-center"
                          >
                            Preview Stylepack<sup>TM</sup>
                          </Text>
                        </Button>
                      </Link>
                      <Button
                        loading={saving}
                        design="LocalBttnSec"
                        designSize="medium"
                        onClick={() => chooseStylepack(pack.stylePack)}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                  <Text className="H5 margin-t-12">{pack.name}</Text>
                </div>
              );
            }
          })}
        </Container>
      </Container>
    </Portal>
  );
}
